import React, { useState, useEffect, useRef } from 'react';
import {
    Shield,
    Heart,
    Flame,
    HandHeart,
    Leaf,
    Star,
    Trophy,
    Truck,
    Lightbulb,
    Users,
    ArrowUpRight
} from 'lucide-react';

const ValuesSection = () => {
    const [isVisible, setIsVisible] = useState(false);
    const [activeCard, setActiveCard] = useState(null);
    const sectionRef = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                }
            },
            { threshold: 0.1 }
        );

        if (sectionRef.current) {
            observer.observe(sectionRef.current);
        }

        return () => observer.disconnect();
    }, []);

    const coreValues = [
        {
            id: 1,
            title: "Integrity",
            description: "Doing what's right, being honest, and transparent in all dealings.",
            icon: Shield,
            accent: "#3B82F6"
        },
        {
            id: 2,
            title: "Commitment",
            description: "Being dedicated to customers, stakeholders, and delivering exceptional service.",
            icon: Heart,
            accent: "#EF4444"
        },
        {
            id: 3,
            title: "Passion",
            description: "Caring deeply about the work, customers, and providing excellent solutions.",
            icon: Flame,
            accent: "#F97316"
        },
        {
            id: 4,
            title: "Trust",
            description: "Building strong relationships, being reliable, and maintaining confidentiality.",
            icon: HandHeart,
            accent: "#8B5CF6"
        },
        {
            id: 5,
            title: "Safety & Environment",
            description: "Prioritizing well-being and reducing environmental impact.",
            icon: Leaf,
            accent: "#10B981"
        },
        {
            id: 6,
            title: "Excellence",
            description: "Striving for perfection, continuously improving, and innovating.",
            icon: Star,
            accent: "#F59E0B"
        }
    ];

    const commitments = [
        {
            id: 1,
            title: "Fleet-First Philosophy",
            description: "Every decision prioritizes fleet owners and drivers, ensuring their success drives our success.",
            icon: Truck,
            number: "01"
        },
        {
            id: 2,
            title: "Innovation for Drivers",
            description: "Pioneering technology and solutions that directly empower drivers and enhance their experience.",
            icon: Lightbulb,
            number: "02"
        },
        {
            id: 3,
            title: "Beyond Solutions",
            description: "Going the extra mile to deliver comprehensive solutions that exceed fleet owner expectations.",
            icon: Users,
            number: "03"
        }
    ];

    return (
        <section
            ref={sectionRef}
            className="w-full pt-16 pb-16 bg-gray-50 relative overflow-hidden"
            aria-labelledby="values-section-title"
        >
            {/* Background Elements */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">

                {/* Section Header */}
                <div
                    className={`mb-20 transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                        }`}
                >
                    <div className="max-w-3xl">

                        <h2
                            id="values-section-title"
                            className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-8 leading-tight"
                        >
                            Values that drive
                            <br />
                            <span className="text-[#F05A28] italic">everything we do</span>
                        </h2>

                        <p className="text-xl text-gray-600 leading-relaxed">
                            These aren't just words on a wall. They're the principles embedded in every interaction,
                            every decision, and every mile we help our partners travel.
                        </p>
                    </div>
                </div>

                {/* Core Values - Asymmetric Layout */}
                <div className="mb-24">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
                        {/* First 3 values - Top row */}
                        {coreValues.slice(0, 3).map((value, index) => {
                            const IconComponent = value.icon;
                            const isActive = activeCard === `value-${value.id}`;

                            return (
                                <div
                                    key={value.id}
                                    className={`group relative transform transition-all duration-700 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-16 opacity-0'
                                        }`}
                                    style={{ transitionDelay: `${0.4 + index * 0.15}s` }}
                                    onMouseEnter={() => setActiveCard(`value-${value.id}`)}
                                    onMouseLeave={() => setActiveCard(null)}
                                >
                                    <div className="relative bg-white rounded-3xl p-8 h-full shadow-sm hover:shadow-xl transition-all duration-500 border border-gray-100 group-hover:border-gray-200">

                                        {/* Accent line */}
                                        <div
                                            className="absolute top-0 left-8 w-16 h-1 rounded-full transition-all duration-500"
                                            style={{ backgroundColor: value.accent }}
                                        ></div>

                                        <div className="pt-6">
                                            <div className="mb-6">
                                                <div
                                                    className="w-14 h-14 rounded-2xl flex items-center justify-center transition-all duration-500"
                                                    style={{
                                                        backgroundColor: isActive ? `${value.accent}15` : '#F9FAFB',
                                                        color: isActive ? value.accent : '#6B7280'
                                                    }}
                                                >
                                                    <IconComponent className="w-7 h-7" />
                                                </div>
                                            </div>

                                            <h3 className="text-2xl font-bold text-gray-900 mb-4">
                                                {value.title}
                                            </h3>

                                            <p className="text-gray-600 leading-relaxed">
                                                {value.description}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Bottom row - Offset grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:ml-24">
                        {coreValues.slice(3).map((value, index) => {
                            const IconComponent = value.icon;
                            const isActive = activeCard === `value-${value.id}`;

                            return (
                                <div
                                    key={value.id}
                                    className={`group relative transform transition-all duration-700 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-16 opacity-0'
                                        }`}
                                    style={{ transitionDelay: `${0.85 + index * 0.15}s` }}
                                    onMouseEnter={() => setActiveCard(`value-${value.id}`)}
                                    onMouseLeave={() => setActiveCard(null)}
                                >
                                    <div className="relative bg-white rounded-3xl p-8 h-full shadow-sm hover:shadow-xl transition-all duration-500 border border-gray-100 group-hover:border-gray-200">

                                        {/* Accent line */}
                                        <div
                                            className="absolute top-0 left-8 w-16 h-1 rounded-full transition-all duration-500"
                                            style={{ backgroundColor: value.accent }}
                                        ></div>

                                        <div className="pt-6">
                                            <div className="mb-6">
                                                <div
                                                    className="w-14 h-14 rounded-2xl flex items-center justify-center transition-all duration-500"
                                                    style={{
                                                        backgroundColor: isActive ? `${value.accent}15` : '#F9FAFB',
                                                        color: isActive ? value.accent : '#6B7280'
                                                    }}
                                                >
                                                    <IconComponent className="w-7 h-7" />
                                                </div>
                                            </div>

                                            <h3 className="text-2xl font-bold text-gray-900 mb-4">
                                                {value.title}
                                            </h3>

                                            <p className="text-gray-600 leading-relaxed">
                                                {value.description}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Our Commitment - Full Width Cards */}
                <div
                    className={`transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                        }`}
                    style={{ transitionDelay: '1.2s' }}
                >
                    <div className="mb-8">

                        <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                            Beyond business,<br />
                            <span className="text-[#F05A28]">it's personal</span>
                        </h3>

                        <p className="text-lg text-gray-600 max-w-3xl">
                            These commitments aren't just strategic goals—they're personal promises to every fleet owner,
                            every driver, and every partner who trusts us with their success.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {commitments.map((commitment, index) => {
                            const IconComponent = commitment.icon;
                            const isActive = activeCard === `commitment-${commitment.id}`;

                            return (
                                <div
                                    key={commitment.id}
                                    className={`group relative flex flex-col h-full transform transition-all duration-700 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-16 opacity-0'}`}
                                    style={{ transitionDelay: `${1.4 + index * 0.2}s` }}
                                    onMouseEnter={() => setActiveCard(`commitment-${commitment.id}`)}
                                    onMouseLeave={() => setActiveCard(null)}
                                >

                                    <div className="relative bg-white rounded-3xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 border border-gray-100 group-hover:border-gray-200 flex flex-col h-full">

                                        {/* Background Pattern */}
                                        <div className="absolute inset-0 bg-gradient-to-r from-[#F05A28]/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                                        <div className="relative p-8 md:p-12 flex items-center">
                                            <div className="flex-1">
                                                <div className="flex items-center mb-6">
                                                    <span className="text-6xl font-bold text-gray-100 mr-6">{commitment.number}</span>
                                                    <div
                                                        className="w-16 h-16 rounded-2xl flex items-center justify-center transition-all duration-500"
                                                        style={{
                                                            backgroundColor: isActive ? '#F05A28' : '#F9FAFB',
                                                            color: isActive ? 'white' : '#F05A28'
                                                        }}
                                                    >
                                                        <IconComponent className="w-8 h-8" />
                                                    </div>
                                                </div>

                                                <h4 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                                                    {commitment.title}
                                                </h4>

                                                <p className="text-lg text-gray-600 leading-relaxed max-w-2xl">
                                                    {commitment.description}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Enhanced SEO Structured Data */}
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                    __html: JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "Organization",
                        "name": "RoadExpress",
                        "description": "Leading logistics platform guided by core values and unwavering commitment to fleet owners and drivers",
                        "values": coreValues.map(value => value.title).join(", "),
                        "mission": "To prioritize fleet owners and drivers while delivering innovative logistics solutions that go beyond expectations",
                        "foundingPrinciples": [
                            "Fleet-First Philosophy",
                            "Driver Innovation",
                            "Solution Excellence"
                        ]
                    })
                }}
            />
        </section>
    );
};

export default ValuesSection;